import React, { useMemo } from 'react';
import AmaSeriesListItem from './AmaSeriesListItem';
import AssignmentListItem from './AssignmentListItem';
import EvaluationListItem from './EvaluationListItem';
import TopicListItem from './TopicListItem';
import { AmaSeries, AssignmentSummary, EvaluationSummary, TopicData } from '@/types';

interface TopicsListProps {
    topics: TopicData[];
    evaluations?: EvaluationSummary[];
    assignments?: AssignmentSummary[];
    amaSeries?: AmaSeries[];
    courseSlug: string;
    isExpanded: boolean;
}

const TopicsList: React.FC<TopicsListProps> = ({
    topics,
    evaluations = [],
    assignments = [],
    amaSeries = [],
    courseSlug,
    isExpanded,
}) => {
    if (!isExpanded) {
        return null;
    }

    const {
        topicAssignmentsByTopicId,
        sectionAssignments,
        topicEvaluationsByTopicId,
        sectionEvaluations,
    } = useMemo(() => {
        const byTopicId = new Map<number, AssignmentSummary[]>();
        const sectionLevel: AssignmentSummary[] = [];
        const evaluationsByTopicId = new Map<number, EvaluationSummary[]>();
        const sectionLevelEvaluations: EvaluationSummary[] = [];

        assignments.forEach((assignment) => {
            if (assignment.topic_id) {
                byTopicId.set(assignment.topic_id, [
                    ...(byTopicId.get(assignment.topic_id) || []),
                    assignment,
                ]);
            } else {
                sectionLevel.push(assignment);
            }
        });

        evaluations.forEach((evaluation) => {
            if (evaluation.topic_id) {
                evaluationsByTopicId.set(evaluation.topic_id, [
                    ...(evaluationsByTopicId.get(evaluation.topic_id) || []),
                    evaluation,
                ]);
            } else {
                sectionLevelEvaluations.push(evaluation);
            }
        });

        return {
            topicAssignmentsByTopicId: byTopicId,
            sectionAssignments: sectionLevel,
            topicEvaluationsByTopicId: evaluationsByTopicId,
            sectionEvaluations: sectionLevelEvaluations,
        };
    }, [assignments, evaluations]);

    const orderedTopics = useMemo(() => {
        const topicsWithoutAssignments = topics.filter(
            (topic) => (topicAssignmentsByTopicId.get(topic.id) || []).length === 0
        );
        const topicsWithAssignments = topics.filter(
            (topic) => (topicAssignmentsByTopicId.get(topic.id) || []).length > 0
        );

        return [...topicsWithoutAssignments, ...topicsWithAssignments];
    }, [topics, topicAssignmentsByTopicId]);

    return (
        <div className="border-t border-gray-100 bg-gray-50">
            <div className="px-6 py-3">
                <div className="space-y-2">
                    {/* Render Topics */}
                    {orderedTopics.map((topic) => (
                        <React.Fragment key={`topic-${topic.id}`}>
                            <TopicListItem
                                topic={topic}
                                courseSlug={courseSlug}
                            />
                            {(topicAssignmentsByTopicId.get(topic.id) || []).map(
                                (assignment) => (
                                    <AssignmentListItem
                                        key={`topic-assignment-${topic.id}-${assignment.id}`}
                                        assignment={assignment}
                                    />
                                )
                            )}
                            {(topicEvaluationsByTopicId.get(topic.id) || []).map(
                                (evaluation) => (
                                    <EvaluationListItem
                                        key={`topic-evaluation-${topic.id}-${evaluation.id}`}
                                        evaluation={evaluation}
                                    />
                                )
                            )}
                        </React.Fragment>
                    ))}

                    {/* Render section-level evaluations */}
                    {sectionEvaluations.map((evaluation) => (
                        <EvaluationListItem
                            key={`evaluation-${evaluation.id}`}
                            evaluation={evaluation}
                        />
                    ))}

                    {/* Render AMA Series */}
                    {amaSeries.map((series) => (
                        <AmaSeriesListItem
                            key={`ama-${series.id}`}
                            amaSeries={series}
                            courseSlug={courseSlug}
                        />
                    ))}

                    {/* Render section-level assignments at the end of the section */}
                    {sectionAssignments.map((assignment) => (
                        <AssignmentListItem
                            key={`assignment-${assignment.id}`}
                            assignment={assignment}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TopicsList;
